import axios, { type AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import env from '../config/env';
import { errorHandler } from './errorHandlerService';
import { isRequestCanceled } from '../utils/requestCancel';

// Flag to prevent multiple redirects
let isRedirecting = false;

// Token storage keys
const TOKEN_KEY = 'x-access-token';
const REDIRECT_URL_KEY = 'auth_redirect_url';

// HTTP status codes that trigger auth redirect
const AUTH_ERROR_CODES = [401, 406] as const;

/**
 * Clear all session cookies with the given name from all paths
 * This is necessary because the server might set cookies with different paths
 */
const clearAllSessionCookies = () => {
  const cookieNames = ['maudInfraGeo.sid', 'connect.sid'];
  const paths = [
    '/', '/apiv1', '/apiv1/',
    '/apiv1/auth', '/apiv1/auth/', '/apiv1/auth/login',
    '/apiv1/erp-admin', '/apiv1/erp-auth', '/apiv1/crda-auth', '/apiv1/apufidc-auth',
    '/apiv1/trade-license', '/apiv1/trade-license/', '/apiv1/trade-license/boundary'
  ];

  cookieNames.forEach(cookieName => {
    paths.forEach(path => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${window.location.hostname}`;
    });
  });
};

/**
 * Module UDA scopes, read straight from localStorage rather than from the zustand stores
 * that own them: those stores reach the auth context, which reaches this file, so an
 * import the other way would close a cycle. The keys and the shape are zustand-persist's
 * own — keep them in step with WMS_UDA_STORAGE_KEY (WmsUdaScope.tsx) and
 * HELPDESK_UDA_STORAGE_KEY (HelpdeskUdaScope.tsx).
 *
 * Each module owns its own pick on purpose: the UDA someone is reading works for says
 * nothing about the UDA whose tickets they want next.
 */
const WMS_UDA_HEADER = 'x-wms-uda';
const WMS_UDA_STORAGE_KEY = 'wms-uda-scope';
const HELPDESK_UDA_HEADER = 'x-helpdesk-uda';
const HELPDESK_UDA_STORAGE_KEY = 'helpdesk-uda-scope';

// Path tests rather than regex literals: the url is relative to the apiv1 base, so each
// module's endpoints start with its own segment and nothing else contains it.
const isWmsRequest = (url?: string): boolean => {
  const u = String(url || '').replace(/^\/+/, '');
  return u === 'ds-wms' || u.startsWith('ds-wms/') || u.includes('/ds-wms/');
};

// The IT Helpdesk API is mounted at /apiv1/helpdesk.
const isHelpdeskRequest = (url?: string): boolean => {
  const u = String(url || '').replace(/^\/+/, '');
  return u === 'helpdesk' || u.startsWith('helpdesk/') || u.includes('/helpdesk/');
};

const readUdaScope = (storageKey: string): number | null => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const udaId = JSON.parse(raw)?.state?.udaId;
    return typeof udaId === 'number' && Number.isFinite(udaId) ? udaId : null;
  } catch {
    // Private windows and blocked site data throw on read; an unscoped request is the
    // same thing the user gets before they have picked, so fall through quietly.
    return null;
  }
};

// Create axios instance with base configuration
const axiosInstance = axios.create({
  baseURL: `${env.API_BASE_URL}/apiv1/`,
  withCredentials: true,
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request Interceptor
 * Adds authentication token to all outgoing requests
 * Handles FormData by removing Content-Type header (browser will set it with boundary)
 */
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const accessToken = localStorage.getItem(TOKEN_KEY);

    if (accessToken && config.headers) {
      config.headers[TOKEN_KEY] = accessToken;
    }

    // For FormData, delete Content-Type header so browser sets it correctly with boundary
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }

    // Module page scopes. Each module's screens sit under one UDA dropdown, and the
    // server reads the pick from these headers — ranking it above the JWT for WMS, and
    // as a narrowing-only request for the Helpdesk. Attached here so no page or service
    // can forget one: the scope has to reach reads, writes and exports alike.
    if (config.headers) {
      // WMS sends 0 for the "All UDAs" pick rather than omitting the header: an absent
      // header falls through to the JWT's udaId server-side (resolveUdaScope), which
      // NARROWS a state user to their own authority. A UDA login is pinned server-side
      // regardless of this header, so 0 cannot widen their access.
      if (isWmsRequest(config.url)) {
        const wmsUda = readUdaScope(WMS_UDA_STORAGE_KEY);
        config.headers[WMS_UDA_HEADER] = String(wmsUda ?? 0);
      }
      const helpdeskUda = readUdaScope(HELPDESK_UDA_STORAGE_KEY);
      if (helpdeskUda !== null && isHelpdeskRequest(config.url)) {
        config.headers[HELPDESK_UDA_HEADER] = String(helpdeskUda);
      }
    }

    return config;
  },
  (error: AxiosError): Promise<never> => {
    console.error('Request interceptor error:', error.message);
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * Handles token refresh from response headers and authentication errors
 */
axiosInstance.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    // Update token if new one is provided in response headers
    const newToken = response.headers[TOKEN_KEY];
    if (newToken) {
      localStorage.setItem(TOKEN_KEY, newToken);
    }

    // Skip JSON status check for blob/arraybuffer responses (file downloads)
    const isFileDownload = response.config?.responseType === 'blob' || response.config?.responseType === 'arraybuffer';

    // This will display error toasts automatically for all API failures
    if (!isFileDownload && response?.data?.status !== 200 && response?.data?.success !== true) {
      // Extract specific error message from errors array/object if available
      const errors = response?.data?.errors;
      const specificMsg = Array.isArray(errors)
        ? errors[0]?.message
        : errors?.[0]?.message || (typeof errors === 'object' && errors?.message);
      throw Error(specificMsg || response?.data?.message || 'Error occurred while processing your request.')
    } else {
      // Reset server-down detection on successful response
      errorHandler.resetServerDown();
    }

    return response;
  },
  (error: AxiosError): Promise<never> => {
    /*
     * A cancelled request is not a failure and must leave this function untouched.
     * A screen that refetches on every scope/filter change (the employee dashboard)
     * aborts the request it is replacing, and an abort rejects with no `response` —
     * which every branch below reads as "the server is unreachable". Left to run, it
     * would raise an "Unable to connect to server" toast on each UDA switch, feed the
     * server-down detector, and overwrite `error.message` so the caller could no
     * longer tell a deliberate abort from a real network failure. Reject as-is and
     * let the caller recognise it via isRequestCanceled().
     */
    if (isRequestCanceled(error)) {
      return Promise.reject(error);
    }

    // Handle error through centralized error handler
    // This will display error toasts automatically for all API failures
    if (!errorHandler.shouldSuppressError(error)) {
      errorHandler.handleError(error);
    }

    // Extract server error message if available
    const responseData = error.response?.data as Record<string, unknown> | string | undefined;
    const serverMessage = typeof responseData === 'string'
      ? responseData
      : (responseData?.message || responseData?.error || responseData?.msg) as string | undefined;

    // Create user-friendly message for network/timeout errors
    let friendlyMessage: string = '';

    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      friendlyMessage = 'Request timed out. The server is taking too long to respond. Please try again.';
    } else if (error.code === 'ERR_NETWORK' || !error.response) {
      friendlyMessage = 'Unable to connect to server. Please check your internet connection.';
    } else if (error.response?.status === 500 && !serverMessage) {
      friendlyMessage = 'Server error. Please try again later or contact support.';
    } else if (error.response?.status === 503 && !serverMessage) {
      friendlyMessage = 'Service temporarily unavailable. Please try again later.';
    } else {
      friendlyMessage = serverMessage || 'An unexpected error occurred';
    }

    // Override error.message with user-friendly message
    error.message = friendlyMessage;

    /*
     * Every path below rejects with `error.response` so that callers can read
     * `.status` / `.data` off it — but an AxiosResponse carries no `message`,
     * so `catch (e) { e.message }` in a component came back undefined and the
     * screen fell through to its own generic text ("Failed to save …"). The
     * real reason (e.g. "Session expired or invalid. Please login again.") was
     * thrown away right here. Copy it onto the response before rejecting.
     */
    if (error.response) {
      (error.response as any).message = friendlyMessage;
      (error.response as any).code = error.code;

      const { status } = error.response;
      // console.error('Response interceptor error:', error.response);

      // Handle authentication errors
      if (AUTH_ERROR_CODES.includes(status as typeof AUTH_ERROR_CODES[number]) && !isRedirecting) {
        const currentPath = window.location.pathname + window.location.search;
        const requestUrl = error.config?.url || '';

        // ✅ Skip redirect if already on login page
        if (currentPath.startsWith('/login') || currentPath.startsWith('/app/apufidc/login') || currentPath.startsWith('/app/use-cases/login')) {
          // Just reject the error, don't redirect
          return Promise.reject(error.response || error);
        }

        // ✅ Skip redirect for auth verification endpoints - let AuthContext handle these
        if (requestUrl.includes('/verify') || requestUrl.includes('/select-tenant')) {
          return Promise.reject(error.response || error);
        }

        // ✅ Skip redirect for master data endpoints - let components handle these errors gracefully
        // These are dropdown/lookup data that shouldn't trigger a logout if they fail
        const masterDataPatterns = [
          '/category', '/sub-category', '/status', '/app-type',
          '/nature-of-business', '/uom', '/fee-type', '/document-type',
          '/labour-classification', '/labour-employer', '/configuration',
          '/boundary/', '/localities', '/wards', '/election-wards', '/ward-sachivalayams',
          '/fee-matrix', '/generator/', '/list',
          '/no-code/masters/', '/appstore/app', '/appstore/category',
          // Digital Twin endpoints - prevent logout on refresh while session validates
          '/digital-twin/', '/erp-map/'
        ];
        if (masterDataPatterns.some(pattern => requestUrl.includes(pattern))) {
          console.warn('Master data API returned auth error, skipping redirect:', requestUrl);
          return Promise.reject(error.response || error);
        }

        isRedirecting = true;

        // Preserve current URL for redirect after login
        localStorage.setItem(REDIRECT_URL_KEY, currentPath);

        // Get auth type before clearing it to determine which login page to use
        const authType = localStorage.getItem('auth_type');

        // Clear all session cookies from all paths
        clearAllSessionCookies();

        // Clear all auth data
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem('user_data');
        localStorage.removeItem('auth_type');
        localStorage.removeItem('user_permissions_cache');
        localStorage.removeItem('user_permissions_timestamp');

        // Redirect to appropriate login page with redirect parameter
        const redirectParam = encodeURIComponent(currentPath);
        if (authType === 'apufidc') {
          window.location.href = `/app/apufidc/login?redirect=${redirectParam}`;
        } else if (authType === 'crda') {
          window.location.href = `/app/use-cases/login?redirect=${redirectParam}`;
        } else {
          window.location.href = `/login?redirect=${redirectParam}`;
        }

        // Reset redirect flag after delay to handle future sessions
        setTimeout(() => {
          isRedirecting = false;
        }, 5000);
      }
    } else if (error.request) {
      // Network error - request was made but no response received
      console.error('Network error: No response received', error.message);
    } else {
      // Error in request configuration
      console.error('Request configuration error:', error.message);
    }

    return Promise.reject(error.response || error);
  }
);

export default axiosInstance;

// Export types for external use
export type { AxiosError, AxiosResponse, InternalAxiosRequestConfig };
